import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { toNumber } from '@/lib/number';
import { Cloud, CloudRain, CloudSun, Sun, Thermometer, Droplets, Wind } from 'lucide-react';
import type { WeatherTodayResponse } from '@/types/api';

interface WeatherWidgetProps {
  farmId: string;
}

export function WeatherWidget({ farmId }: WeatherWidgetProps) {
  const { data: weather, isLoading, error } = useQuery<WeatherTodayResponse>({
    queryKey: ['weather', farmId, 'today'],
    queryFn: () => api.get(`/api/v1/farms/${farmId}/weather/today`),
    // An available:false "loading" response must not stick: the backend is fetching the forecast in the background.
    staleTime: (query) => (query.state.data?.available ? 30 * 60 * 1000 : 0),
    refetchInterval: (query) => {
      const d = query.state.data;
      return d && !d.available && !/location not set/i.test(d.message ?? '') ? 15_000 : false;
    },
    retry: 1,
  });

  if (isLoading) {
    return (
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 p-6 animate-pulse">
        <div className="h-20" />
      </div>
    );
  }

  if (error || !weather?.available) {
    // Issue #127: distinguish "farm has no coords" (a 200 available:false) from "weather
    // service down / still loading" so the user knows whether action from them will fix it.
    const serverMessage = !error ? weather?.message : null;
    const noCoords = /location not set/i.test(serverMessage ?? '');
    // No instruction for a missing location: there is no screen where a farm's coordinates are set.
    const title = noCoords ? 'Weather not set up yet' : 'Weather Temporarily Unavailable';
    const body = noCoords ? null : serverMessage ?? 'Forecast service unreachable — will retry automatically';
    return (
      <div className="relative overflow-hidden rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50/50 p-6">
        <div className="flex items-center gap-4 text-slate-500">
          <Cloud className="h-8 w-8 opacity-40" />
          <div>
            <p className="font-medium">{title}</p>
            {body && <p className="text-sm opacity-70">{body}</p>}
          </div>
        </div>
      </div>
    );
  }

  const w = weather.weather;
  const fr = weather.feed_recommendation ?? null;

  // Early return if weather data is missing (shouldn't happen if available=true)
  if (!w) {
    return (
      <div className="relative overflow-hidden rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50/50 p-6">
        <div className="flex items-center gap-4 text-slate-500">
          <Cloud className="h-8 w-8 opacity-40" />
          <div>
            <p className="font-medium">Weather Data Missing</p>
            <p className="text-sm opacity-70">Try refreshing the page</p>
          </div>
        </div>
      </div>
    );
  }

  const adjustmentPercent = fr ? toNumber(fr.adjustment_percent, 0) : 0;
  const hasAdjustment = adjustmentPercent !== 0;
  const maxTemp = toNumber(w.temperature_max_c, 0);
  const precipProb = toNumber(w.precipitation_probability, 0);
  const precipMm = toNumber(w.precipitation_mm, 0);

  // Determine sky gradient based on conditions
  const getSkyGradient = () => {
    const desc = w.description.toLowerCase();
    if (desc.includes('rain') || desc.includes('drizzle') || desc.includes('thunder')) {
      return 'from-slate-400 via-slate-500 to-slate-600';
    }
    if (desc.includes('overcast') || desc.includes('fog')) {
      return 'from-slate-300 via-slate-400 to-slate-500';
    }
    if (desc.includes('cloud')) {
      return 'from-sky-300 via-sky-400 to-slate-400';
    }
    // Clear/sunny
    return 'from-sky-400 via-sky-500 to-blue-600';
  };

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-2xl p-6',
        'bg-gradient-to-br',
        getSkyGradient(),
        'shadow-lg shadow-sky-500/20',
        'transition-all duration-500'
      )}
    >
      {/* Atmospheric overlay for depth */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent" />

      {/* Cloud decoration for rainy days */}
      {w.description.toLowerCase().includes('rain') && (
        <div className="absolute -right-8 -top-8 opacity-20">
          <CloudRain className="h-32 w-32 text-white" />
        </div>
      )}

      <div className="relative flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
        {/* Weather Icon & Description */}
        <div className="flex items-center gap-4 flex-1">
          <div className="rounded-xl bg-white/20 backdrop-blur-sm p-3">
            <WeatherIcon description={w.description} className="h-10 w-10 text-white" />
          </div>
          <div>
            <p className="font-semibold text-white text-lg drop-shadow-sm">
              {w.description}
            </p>
            <div className="flex items-center gap-4 text-white/90 text-sm mt-1">
              <span className="flex items-center gap-1.5">
                <Thermometer className="h-4 w-4" />
                {maxTemp}°C
              </span>
              <span className="flex items-center gap-1.5">
                <Droplets className="h-4 w-4" />
                {precipProb}%
              </span>
              {precipMm > 0 && (
                <span className="flex items-center gap-1.5">
                  <Wind className="h-4 w-4" />
                  {precipMm}mm
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Feed Recommendation */}
        {hasAdjustment && fr && (
          <div className="flex items-center gap-3 bg-white/95 backdrop-blur rounded-xl px-4 py-3 shadow-lg">
            <div
              className={cn(
                'flex items-center justify-center w-12 h-12 rounded-lg font-bold text-lg',
                adjustmentPercent <= -30
                  ? 'bg-status-critical/10 text-status-critical'
                  : adjustmentPercent < 0
                    ? 'bg-status-warning/10 text-status-warning'
                    : 'bg-status-ok/10 text-status-ok'
              )}
            >
              {adjustmentPercent > 0 ? '+' : ''}{adjustmentPercent}%
            </div>
            <div className="text-sm">
              <p className="font-medium text-gray-900">Feed Adjustment</p>
              <p className="text-gray-600 max-w-[180px] truncate">{fr.reasons?.[0]}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function WeatherIcon({ description, className }: { description: string; className?: string }) {
  const desc = description.toLowerCase();

  if (desc.includes('rain') || desc.includes('drizzle') || desc.includes('shower')) {
    return <CloudRain className={className} />;
  }
  if (desc.includes('thunder') || desc.includes('storm')) {
    return <CloudRain className={className} />;
  }
  if (desc.includes('overcast') || desc.includes('fog') || desc.includes('mist')) {
    return <Cloud className={className} />;
  }
  if (desc.includes('cloud') || desc.includes('partly')) {
    return <CloudSun className={className} />;
  }
  return <Sun className={className} />;
}
